import { SvgIcon, SvgIconProps } from "@mui/material";
import { FC, SVGProps } from "react";

interface Props extends SvgIconProps {
  icon: FC<SVGProps<SVGSVGElement>>;
}

/**
 * IconWrapper is a wrapper for SVG icons that allows for easy customization.
 *
 * ## Usage
 *
 * Pass your SVG as the `icon` prop:
 * ```tsx
 * import { ReactComponent as SendIconSvg } from '../../common/icons/send.svg';
 * <IconWrapper icon={SendIconSvg} />
 * ```
 *
 * ## Color customization
 *
 * - **Themed colors**: use the `sx` prop to set `color`, for example:
 *   ```tsx
 *   <IconWrapper
 *     icon={SendIconSvg}
 *     sx={{ color: 'primary.main' }}
 *   />
 *   ```
 *
 * - **Override SVG path**: target the `<path>` element via `sx`, for example:
 *   ```tsx
 *   <IconWrapper
 *     icon={SendIconSvg}
 *     sx={{
 *       '& path': { fill: 'red', stroke: 'blue' },
 *     }}
 *   />
 *   ```
 *
 * - **htmlColor prop**: pass an `htmlColor` to set the fill directly:
 *   ```tsx
 *   <IconWrapper
 *     icon={SendIconSvg}
 *     htmlColor="green"
 *   />
 *   ```
 *
 * All other `SvgIconProps` such as `fontSize` and `inheritViewBox` are forwarded.
 */

const IconWrapper = ({ icon, sx = {}, inheritViewBox = true }: Props) => (
  <SvgIcon component={icon} inheritViewBox={inheritViewBox} sx={sx} />
);

export default IconWrapper;
